/**
 * Time Control System - Preset Configurations
 *
 * Defines the standard time control presets for Zodiac Chess.
 */

import { TimeControlPreset, TimeControlConfig } from './types';

/**
 * Standard time control presets
 *
 * - OFF: No time limit (casual/tutorial mode)
 * - SPRINT: 1 minute + 2 second increment (fast-paced)
 * - STANDARD: 3 minutes + 4 second increment (balanced, most popular)
 * - MARATHON: 10 minutes + 10 second increment (strategic depth)
 * - ANY: Matchmaking only - accepts any time control
 */
export const TIME_PRESETS: Record<TimeControlPreset, TimeControlConfig> = {
  [TimeControlPreset.OFF]: {
    preset: TimeControlPreset.OFF,
    initialTime: 0,
    increment: 0
  },
  [TimeControlPreset.SPRINT]: {
    preset: TimeControlPreset.SPRINT,
    initialTime: 60000,      // 1 minute
    increment: 2000          // 2 seconds
  },
  [TimeControlPreset.STANDARD]: {
    preset: TimeControlPreset.STANDARD,
    initialTime: 180000,     // 3 minutes
    increment: 4000          // 4 seconds
  },
  [TimeControlPreset.MARATHON]: {
    preset: TimeControlPreset.MARATHON,
    initialTime: 600000,     // 10 minutes
    increment: 10000         // 10 seconds
  },
  [TimeControlPreset.ANY]: {
    preset: TimeControlPreset.ANY,
    initialTime: 0,          // Will be set by matched opponent
    increment: 0
  }
};

/**
 * Get a time control config by preset
 */
export function getPresetConfig(preset: TimeControlPreset): TimeControlConfig {
  return TIME_PRESETS[preset];
}

/**
 * Check if a preset has time limits
 */
export function isTimedPreset(preset: TimeControlPreset): boolean {
  return preset !== TimeControlPreset.OFF && preset !== TimeControlPreset.ANY;
}

/**
 * Format time in MM:SS for display
 */
export function formatTime(milliseconds: number): string {
  const totalSeconds = Math.ceil(milliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Get display name for preset
 */
export function getPresetDisplayName(preset: TimeControlPreset): string {
  const names: Record<TimeControlPreset, string> = {
    [TimeControlPreset.OFF]: 'Off',
    [TimeControlPreset.SPRINT]: 'Sprint',
    [TimeControlPreset.STANDARD]: 'Standard',
    [TimeControlPreset.MARATHON]: 'Marathon',
    [TimeControlPreset.ANY]: 'Any'
  };
  return names[preset];
}

/**
 * Get preset description (time + increment)
 */
export function getPresetDescription(preset: TimeControlPreset): string {
  const config = TIME_PRESETS[preset];

  if (preset === TimeControlPreset.OFF) {
    return 'No time limit';
  }

  if (preset === TimeControlPreset.ANY) {
    return 'Match any time control';
  }

  const minutes = config.initialTime / 60000;
  const incrementSeconds = config.increment / 1000;

  return `${minutes} min + ${incrementSeconds}s`;
}
